import { useLocation } from 'react-router';
import type { PAGE_POSITIONS, PageType } from 'types';
import type {
  ProductListItem,
  ProductListItemExternal,
  ProductListItemInlineExternal,
  ProductListItemInternal
} from 'types/capi/shared';

import ProductNotificationCount from '~/components/ProductNotificationCount';
import AffiliateLinkClickEvent from '~/events/AffiliateLinkClickEvent';
import ProductCardClickEvent from '~/events/ProductCardClickEvent';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import wct from '~/services/wct.client';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

import Image from '../Image';
import ImageMetadata from '../ImageMetadata';
import ProductNotificationButton from '../ProductNotificationButton';
import StockStatus from '../StockStatus';
import Tracked from '../Tracked';
import ClickOutLink from './ClickOutLink';

type ProductImageRatio = '1:1' | '4:5';

function getImageRatio({
  imageRatio,
  listRatio
}: {
  imageRatio: string | undefined;
  listRatio?: ProductImageRatio;
}): ProductImageRatio {
  return listRatio ?? (imageRatio === '4:5' ? '4:5' : '1:1');
}

type Props = {
  allowNotifications: boolean;
  cardSize: 'small' | 'large';
  className?: string;
  imageLoading?: 'lazy' | 'eager';
  listId?: string | null;
  listRatio?: ProductImageRatio;
  name?: TrackedButtonClickName;
  overrideInteractionPagePosition?: boolean;
  padImage: boolean;
  pagePosition: PAGE_POSITIONS;
  showNotificationCount?: boolean;
  withMetadata?: boolean;
};

interface ExternalProps extends Props {
  openPdp?: boolean;
  pageType?: PageType;
  position?: number;
  product: ProductListItemExternal;
}

interface InternalProps extends Props {
  onClick?: () => void;
  pageType: PageType;
  position?: number;
  product: ProductListItemInternal;
}

const ExternalProductCard = ({
  allowNotifications,
  cardSize,
  name,
  className,
  imageLoading,
  listRatio,
  listId = null,
  position,
  openPdp = true,
  overrideInteractionPagePosition = false,
  padImage,
  pagePosition,
  product,
  showNotificationCount = false,
  pageType,
  withMetadata
}: ExternalProps) => {
  return (
    <div className={`relative ${className}`}>
      <ClickOutLink
        affiliate={product.affiliate}
        buttonPosition={0}
        listId={listId}
        position={position}
        name={name}
        pagePosition={pagePosition}
        price={product.price}
        product={product}
        pageType={pageType}
        urlTemplates={product}
        variant="none"
        className="flex cursor-pointer flex-col justify-center gap-3.5 overflow-hidden"
        openPdp={openPdp}
      >
        <CardContents
          allowNotifications={allowNotifications}
          withMetadata={withMetadata}
          cardSize={cardSize}
          listRatio={listRatio}
          padImage={padImage}
          pagePosition={pagePosition}
          product={product}
          imageLoading={imageLoading}
          showNotificationCount={showNotificationCount}
        />
      </ClickOutLink>
      <CardInteraction
        product={product}
        allowNotifications={allowNotifications}
        pagePosition={
          overrideInteractionPagePosition ? pagePosition : 'Product Card'
        }
      />
    </div>
  );
};

const InternalProductCard = ({
  allowNotifications,
  name = 'PRODUCT_CARD',
  pagePosition,
  product,
  padImage,
  cardSize,
  listRatio,
  listId = null,
  className,
  position,
  onClick,
  imageLoading,
  showNotificationCount = false,
  pageType,
  overrideInteractionPagePosition = false,
  withMetadata
}: InternalProps) => {
  const user = useUser();
  const { isBot } = useRootData();

  return (
    <div className={`relative ${className}`}>
      <Tracked.InternalLink
        data-cy="product-card"
        title={product.name}
        aria-label={product.name}
        to={product.path}
        onClick={() => {
          if (!isBot) {
            new ProductCardClickEvent({
              isBot,
              isClickOut: false,
              listId,
              pagePosition,
              pageType,
              position,
              product,
              user
            }).send();
          }

          if (onClick) onClick();
        }}
        className="flex cursor-pointer flex-col justify-center gap-3.5 overflow-hidden"
        variant="none"
        name={name}
        pagePosition={pagePosition}
      >
        <CardContents
          allowNotifications={allowNotifications}
          cardSize={cardSize}
          listRatio={listRatio}
          padImage={padImage}
          pagePosition={pagePosition}
          product={product}
          imageLoading={imageLoading}
          withMetadata={withMetadata}
          showNotificationCount={showNotificationCount}
        />
      </Tracked.InternalLink>
      <CardInteraction
        product={product}
        allowNotifications={allowNotifications}
        pagePosition={
          overrideInteractionPagePosition ? pagePosition : 'Product Card'
        }
      />
    </div>
  );
};

function CardContents({
  product,
  cardSize,
  listRatio,
  padImage,
  imageLoading,
  showNotificationCount,
  withMetadata = true
}: Props & { product: ProductListItemExternal | ProductListItemInternal }) {
  return (
    <>
      <div className="hover:opacity-80">
        <div className="absolute left-2 top-2 lg:left-4 lg:top-4">
          <StockStatus
            status={product.stock_status}
            releaseDate={product.date?.released}
            releaseDateFormat={cardSize === 'large' ? 'long' : 'short'}
          />
        </div>
        <ProductImage
          alt={product.image?.alt}
          url={product.image?.url}
          ratio={getImageRatio({
            imageRatio: product.image?.ratio,
            listRatio
          })}
          padImage={padImage}
          cardSize={cardSize}
          imageLoading={imageLoading}
        />
        {showNotificationCount && product.weekly_notifications_count && (
          <div className="mt-2 bg-white/90">
            <ProductNotificationCount
              count={product.weekly_notifications_count}
            />
          </div>
        )}
      </div>

      {withMetadata && (
        <ImageMetadata
          affiliate={
            product.type === 'external' ? product.affiliate : undefined
          }
          linkType="product"
          price={product.price}
          title={product.name}
          variantCount={
            product.sub_type === 'clothing'
              ? product.num_of_variants
              : undefined
          }
        />
      )}
    </>
  );
}

const ProductImage = ({
  alt,
  cardSize,
  imageLoading,
  padImage,
  ratio = '1:1',
  url
}: {
  alt?: string;
  cardSize: 'small' | 'large';
  imageLoading: Props['imageLoading'];
  padImage: boolean;
  ratio?: ProductImageRatio;
  url?: string;
}) => {
  if (!url) return <ImageSkeleton ratio={ratio} />;

  const ratioConfig = {
    '1:1': {
      className: '',
      lg: { height: 768 as const, width: 768 as const },
      sm: { height: 576 as const, width: 576 as const }
    },
    '4:5': {
      className: 'aspect-[4/5] object-cover',
      lg: { height: 1000 as const, width: 800 as const },
      sm: { height: 720 as const, width: 576 as const }
    }
  }[ratio];

  const { height, width } =
    cardSize === 'large' ? ratioConfig.lg : ratioConfig.sm;

  return (
    <ImageSkeleton ratio={ratio}>
      <Image
        alt={alt}
        src={url}
        height={height}
        width={width}
        pad={padImage}
        quality={100}
        loading={imageLoading}
        className={ratioConfig.className}
        responsiveOptions={{
          lg: { ...ratioConfig.lg, pad: padImage },
          sm: { ...ratioConfig.sm, pad: padImage }
        }}
      />
    </ImageSkeleton>
  );
};

const ImageSkeleton = ({
  children,
  ratio = '1:1'
}: {
  children?: JSX.Element;
  ratio?: ProductImageRatio;
}) => {
  return (
    <div
      className={`flex w-full items-center justify-center bg-white ${
        ratio === '4:5' ? 'aspect-[4/5]' : 'aspect-square'
      }`}
    >
      {children}
    </div>
  );
};

const CardInteraction = ({
  product,
  allowNotifications,
  pagePosition
}: {
  allowNotifications: boolean;
  pagePosition: PAGE_POSITIONS;
  product: ProductListItem;
}) => {
  const notificationsData = {
    id: product.id,
    image: product.image,
    legacyId: product.legacy_id,
    name: product.name,
    price: product.price.sale ?? product.price.regular,
    queryID: product.queryID
  };

  if (allowNotifications) {
    return (
      <div className="absolute right-2 top-[12px] lg:right-4 lg:top-[20px]">
        <ProductNotificationButton
          product={notificationsData}
          pagePosition={pagePosition}
          buttonStyle="compact"
        />
      </div>
    );
  }

  return null;
};

interface InlineExternalProps {
  cardSize: 'small' | 'large';
  className?: string;
  imageLoading?: 'lazy' | 'eager';
  listRatio?: ProductImageRatio;
  name?: TrackedButtonClickName;
  padImage: boolean;
  pagePosition: PAGE_POSITIONS;
  product: ProductListItemInlineExternal;
  withMetadata?: boolean;
}

const InlineExternalProductCard = ({
  cardSize,
  className,
  imageLoading,
  listRatio,
  name,
  padImage,
  pagePosition,
  product,
  withMetadata = true
}: InlineExternalProps) => {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot } = useRootData();
  const { pathname, search } = useLocation();

  const { url, clickRef } = getTrackedUrlAndClickRef(product, { user });

  const onClick = () => {
    if (isBot) return;

    const pagePath = `${env.SITE_BASE_URL}${pathname}${search}`;

    wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath,
      url
    });

    new AffiliateLinkClickEvent({
      affiliateName: product.affiliate?.name ?? 'unknown',
      isBot
    }).send();
  };

  return (
    <div className={`relative ${className}`}>
      <Tracked.ExternalLink
        href={url}
        target="_blank"
        rel="sponsored nofollow noopener"
        name={name ?? `PRODUCT_CARD_${product.name}`}
        pagePosition={pagePosition}
        affiliate={product.affiliate?.name}
        variant="none"
        className="flex cursor-pointer flex-col items-center justify-center gap-3.5 overflow-hidden"
        onClick={onClick}
      >
        <div className="bg-white">
          <div className="hover:opacity-80">
            <ProductImage
              alt={product.image?.alt}
              cardSize={cardSize}
              imageLoading={imageLoading}
              padImage={padImage}
              ratio={getImageRatio({
                imageRatio: product.image?.ratio,
                listRatio
              })}
              url={product.image?.url}
            />
          </div>
        </div>

        {withMetadata && (
          <ImageMetadata
            affiliate={product.affiliate}
            linkType="product"
            price={product.price}
            title={product.name}
          />
        )}
      </Tracked.ExternalLink>
    </div>
  );
};

export default {
  External: ExternalProductCard,
  InlineExternal: InlineExternalProductCard,
  Internal: InternalProductCard
};
